/**
 * Create and edit a vendor — the external contractors work orders assign
 * to, and (step 14) the payees of vendor disbursements.
 *
 * Layout mirrors OwnerForm.tsx: the form on the left, a sidebar on the
 * right for the things that are administrative rather than editorial —
 * portal access and Stripe Connect enrollment.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm } from '@refinedev/core';
import { MICHIGAN, US_STATES, type VendorConnectStatus } from 'shared';
import { api, ApiError } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';
import { Field, FormErrors, TextArea, TextInput, fieldErrors } from '../../../components/Form.js';
import { PortalAccessCard } from '../../../components/PortalAccessCard.js';

interface VendorRecord {
  id: string;
  name: string;
  trade: string | null;
  email: string | null;
  phone: string | null;
  contactPerson: string | null;
  mailingAddress: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
  };
  notes: string | null;
  hasPortalAccount: boolean;
  connect: {
    status: VendorConnectStatus;
    requirementsDue: string[];
    enrolledAt: string | null;
  };
  archivedAt: string | null;
}

interface VendorFormState {
  name: string;
  trade: string;
  email: string;
  phone: string;
  contactPerson: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  notes: string;
}

const EMPTY: VendorFormState = {
  name: '',
  trade: '',
  email: '',
  phone: '',
  contactPerson: '',
  line1: '',
  line2: '',
  city: '',
  state: MICHIGAN,
  postalCode: '',
  notes: '',
};

export function VendorForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<VendorFormState>(EMPTY);
  const [archiveError, setArchiveError] = useState<unknown>(null);
  const [archiving, setArchiving] = useState(false);

  const { onFinish, query, mutation } = useForm<VendorRecord>({
    resource: 'vendors',
    action: isEdit ? 'edit' : 'create',
    ...(isEdit ? { id } : {}),
    redirect: false,
  });

  const record = query?.data?.data;

  useEffect(() => {
    if (!record) return;
    setForm({
      name: record.name,
      trade: record.trade ?? '',
      email: record.email ?? '',
      phone: record.phone ?? '',
      contactPerson: record.contactPerson ?? '',
      line1: record.mailingAddress.line1 ?? '',
      line2: record.mailingAddress.line2 ?? '',
      city: record.mailingAddress.city ?? '',
      state: record.mailingAddress.state ?? MICHIGAN,
      postalCode: record.mailingAddress.postalCode ?? '',
      notes: record.notes ?? '',
    });
  }, [record?.id, record]);

  const set =
    <K extends keyof VendorFormState>(key: K) =>
    (value: VendorFormState[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));

  const errors = fieldErrors(mutation.error);

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    // A partially-filled address is sent as null rather than as an object with
    // holes — same reasoning OwnerForm.tsx's own submit uses.
    const hasAddress = form.line1 !== '' && form.city !== '' && form.postalCode !== '';

    const payload = {
      name: form.name,
      trade: form.trade || null,
      email: form.email || null,
      phone: form.phone || null,
      contactPerson: form.contactPerson || null,
      mailingAddress: hasAddress
        ? {
            line1: form.line1,
            line2: form.line2 || null,
            city: form.city,
            state: form.state,
            postalCode: form.postalCode,
          }
        : null,
      notes: form.notes || null,
    };

    const saved = (await onFinish(payload)) as { data?: VendorRecord } | undefined;
    const savedId = saved?.data?.id ?? id;
    if (savedId && !isEdit) void navigate(`/staff/vendors/${savedId}/edit`);
    else if (!savedId) void navigate('/staff/vendors');
  }

  async function archive() {
    if (!id || !window.confirm(`Archive ${record?.name ?? 'this vendor'}?`)) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      await api(`/vendors/${id}`, { method: 'DELETE' });
      void navigate('/staff/vendors');
    } catch (caught) {
      setArchiveError(caught);
    } finally {
      setArchiving(false);
    }
  }

  if (isEdit && query?.isLoading) return <p className="muted">Loading…</p>;

  const formCard = (
    <form className="stack card" onSubmit={(event) => void submit(event)}>
      <FormErrors error={mutation.error} />

      <p className="form-section-label">Contact</p>

      <Field label="Name" name="name" required error={errors.name}>
        <TextInput name="name" value={form.name} onChange={set('name')} required />
      </Field>

      <Field label="Trade" name="trade" hint="Plumbing, HVAC, Locksmith…" error={errors.trade}>
        <TextInput name="trade" value={form.trade} onChange={set('trade')} />
      </Field>

      <Field label="Email" name="email" error={errors.email}>
        <TextInput name="email" type="email" value={form.email} onChange={set('email')} />
      </Field>

      <Field label="Phone" name="phone" error={errors.phone}>
        <TextInput name="phone" type="tel" value={form.phone} onChange={set('phone')} />
      </Field>

      <Field
        label="Contact person"
        name="contactPerson"
        error={errors.contactPerson}
        hint="For a vendor that's a company — who to reach."
      >
        <TextInput
          name="contactPerson"
          value={form.contactPerson}
          onChange={set('contactPerson')}
        />
      </Field>

      <p className="form-section-label">Mailing address</p>

      <Field label="Street" name="mailingAddress.line1" error={errors['mailingAddress.line1']}>
        <TextInput name="mailingAddress.line1" value={form.line1} onChange={set('line1')} />
      </Field>

      <Field label="Unit / suite" name="mailingAddress.line2">
        <TextInput name="mailingAddress.line2" value={form.line2} onChange={set('line2')} />
      </Field>

      <Field label="City" name="mailingAddress.city" error={errors['mailingAddress.city']}>
        <TextInput name="mailingAddress.city" value={form.city} onChange={set('city')} />
      </Field>

      <Field label="State" name="mailingAddress.state" error={errors['mailingAddress.state']}>
        <select
          id="mailingAddress.state"
          name="mailingAddress.state"
          className="field__input"
          value={form.state}
          onChange={(event) => set('state')(event.target.value)}
        >
          {US_STATES.map((state) => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="ZIP"
        name="mailingAddress.postalCode"
        error={errors['mailingAddress.postalCode']}
      >
        <TextInput
          name="mailingAddress.postalCode"
          value={form.postalCode}
          onChange={set('postalCode')}
        />
      </Field>

      <p className="form-section-label">Notes</p>

      <Field label="Notes" name="notes" error={errors.notes}>
        <TextArea name="notes" value={form.notes} onChange={set('notes')} />
      </Field>

      <div className="row">
        <button type="submit" className="button" disabled={mutation.isLoading}>
          {mutation.isLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Add vendor'}
        </button>
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/vendors')}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <section className="stack">
      <div className="row">
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/vendors')}
        >
          ← All vendors
        </button>
      </div>

      <h1>
        {isEdit ? (record?.name ?? 'Vendor') : 'Add vendor'}
        {record?.archivedAt ? <span className="pill"> Archived</span> : null}
      </h1>

      {isEdit && record && id ? (
        <div className="person-layout">
          {formCard}
          <div className="stack">
            <PortalAccessCard
              resource="vendors"
              id={id}
              hasPortalAccount={record.hasPortalAccount}
              email={record.email ?? ''}
            />
            <ConnectCard
              vendorId={id}
              connect={record.connect}
              hasPortalAccount={record.hasPortalAccount}
            />
          </div>
        </div>
      ) : (
        formCard
      )}

      {isEdit && !record?.archivedAt ? (
        <div className="row">
          <button
            type="button"
            className="button button--quiet"
            disabled={archiving}
            onClick={() => void archive()}
          >
            {archiving ? 'Archiving…' : 'Archive vendor'}
          </button>
        </div>
      ) : null}
      {archiveError ? (
        <p className="alert alert--error">
          {archiveError instanceof Error ? archiveError.message : 'Could not archive that vendor.'}
        </p>
      ) : null}
    </section>
  );
}

const CONNECT_STATUS_LABEL: Record<VendorConnectStatus, string> = {
  NOT_INVITED: 'Not invited',
  INVITED: 'Invited',
  ONBOARDING_STARTED: 'Onboarding started',
  PENDING_VERIFICATION: 'Pending verification',
  ENROLLED: 'Enrolled',
  REQUIREMENTS_DUE: 'Requirements due',
};

/**
 * Direct-deposit enrollment status, staff-facing side — direct mirror of
 * OwnerForm.tsx's ConnectCard. The actual enrollment flow only ever happens
 * through the vendor's own portal, never on staff's behalf.
 *
 * Only one action button ever renders, and which one depends on
 * `hasPortalAccount`, not on `PortalAccessCard`'s own visible button —
 * these two cards are independent, but the vendor-specific rule is: a
 * vendor with no portal account gets no "Invite to enroll" button here at
 * all, since `PortalAccessCard`'s "Send enrollment invite" button already
 * covers that combined case (see connect-service.ts's header). This card's
 * own button ("Resend enrollment link") only makes sense once an account
 * already exists.
 */
function ConnectCard({
  vendorId,
  connect,
  hasPortalAccount,
}: {
  vendorId: string;
  connect: VendorRecord['connect'];
  hasPortalAccount: boolean;
}) {
  const canInvite = useCan('maintenance:write');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function invite() {
    setBusy(true);
    setError(null);
    try {
      await api(`/vendors/${vendorId}/connect/invite`, { method: 'POST' });
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not send the invite.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <h2 style={{ margin: 0 }}>Direct deposit</h2>
      <div className="row">
        <span className="pill">{CONNECT_STATUS_LABEL[connect.status]}</span>
        {connect.enrolledAt ? (
          <span className="muted" style={{ fontSize: '0.8rem' }}>
            since {connect.enrolledAt.slice(0, 10)}
          </span>
        ) : null}
      </div>
      {connect.status === 'REQUIREMENTS_DUE' && connect.requirementsDue.length > 0 ? (
        <ul className="muted" style={{ fontSize: '0.8rem', margin: 0, paddingLeft: '1.2rem' }}>
          {connect.requirementsDue.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="alert alert--error">{error}</p> : null}
      {canInvite &&
      hasPortalAccount &&
      (connect.status === 'NOT_INVITED' ||
        connect.status === 'INVITED' ||
        connect.status === 'ONBOARDING_STARTED' ||
        connect.status === 'PENDING_VERIFICATION') ? (
        <button
          type="button"
          className="button button--quiet"
          disabled={busy || sent}
          onClick={() => void invite()}
        >
          {sent ? 'Link sent' : busy ? 'Sending…' : 'Resend enrollment link'}
        </button>
      ) : null}
      {!hasPortalAccount ? (
        <p className="muted" style={{ fontSize: '0.8rem' }}>
          Enrollment starts once this vendor has a portal account — use &ldquo;Send enrollment
          invite&rdquo; above.
        </p>
      ) : null}
    </div>
  );
}
